import React, { FunctionComponent, ReactElement } from "react";
import {
  GameCodes,
  MatchedLine,
  Point,
  RoundHistoryComponentProps,
  RoundType,
  SpinHistory,
} from "../../type";
import {
  AnimationPoint,
  DearSenpaiGameData,
  FreeSpinMode,
  Symbols,
} from "./type";
import GameUtils from "./utils";

import "./dear-senpai.scss";
import { Link } from "react-router-dom";
import Utils from "../../utils";
import { useFocusSpinId } from "../../hooks";
import { BuyFreeSpinRoundHistoryView } from "../../components";
import BigNumber from "bignumber.js";
import { useTranslator } from "../../context/TranslationContext";

const lineRatio = BigNumber(0.2);
export const DearSenpaiRoundHistoryView: FunctionComponent<
  RoundHistoryComponentProps
> = ({ roundHistory, type, id }) => {
  const focusSpinId = useFocusSpinId(type, id);
  const spin =
    roundHistory.spins.find((s) => s.id === focusSpinId) ??
    roundHistory.spins[0];
  const translator = useTranslator();
  if (!spin) return <>Spin not found</>;

  if (!spin.focus) spin.focus = true;

  const plays = roundHistory.spins.reduce((ps, s) => {
    if (!ps[s.playRefNumber]) ps[s.playRefNumber] = [];
    ps[s.playRefNumber].push(s);
    return ps;
  }, {} as { [k: string]: SpinHistory[] });
  return (
    <BuyFreeSpinRoundHistoryView
      gameCode={GameCodes.DearSenpai}
      roundHistory={roundHistory}
    >
      <div className="card mb-3 sb">
        <div className="card-body">
          <div className="card-title fs-4 fw-bold">
            {translator.get("spin")}
            <button
              type="button"
              className="btn btn-info float-end"
              data-bs-toggle="modal"
              data-bs-target="#explaination"
            >
              <i className="bi bi-info-circle fw-bold"></i>
            </button>
          </div>

          <hr />
          {Object.values(plays).map((s) => (
            <PlayRecord
              key={s[0].playRefNumber}
              spins={s}
              betAmount={+roundHistory.betAmount}
              lineAmount={+roundHistory.lineAmount}
              onSpinClicked={(spinId) => {
                spin.focus = false;
              }}
              buyFreeSpinRate={
                roundHistory.buyFreeSpin ? roundHistory.buyFreeSpinRate : 1
              }
              roundType={roundHistory.roundType}
            />
          ))}
        </div>
      </div>
      <div id="explaination" className={`modal fade`} tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{translator.get("explanation")}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="pb-3 fs-5">{translator.get("formula")}</div>
              <div className="pb-3">
                {translator.get("bet_size")} = {translator.get("bet")} x 0.2
              </div>
              <div className="pb-3">
                {translator.get("payout")} = {translator.get("bet_size")} x{" "}
                {translator.get("multiplier")}
              </div>
              <hr />
              <div className="pb-3 fs-5">{translator.get("multiplier")}</div>
              <table className="w-100">
                <tbody>
                  <tr>
                    {[
                      [21, 100],
                      [201, 50],
                      [202, 25],
                      [203, 15],
                    ].map(([symbol, payout], i) => (
                      <td key={`explanation-${symbol}`} className="text-center">
                        <div className="d-inline-block symbol">
                          {GameUtils.convertSymbol(+symbol, FreeSpinMode.None)}
                        </div>
                        <div className="">{payout}</div>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    {[
                      [204, 10],
                      [205, 5],
                      [206, 3],
                      [207, 2],
                    ].map(([symbol, payout], i) => (
                      <td key={`explanation-${symbol}`} className="text-center">
                        <div className="d-inline-block symbol">
                          {GameUtils.convertSymbol(+symbol, FreeSpinMode.None)}
                        </div>
                        <div className="">{payout}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </BuyFreeSpinRoundHistoryView>
  );
};
const PlayRecord: FunctionComponent<{
  spins: SpinHistory[];
  betAmount: number;
  lineAmount: number;
  onSpinClicked: (id: string) => void;
  buyFreeSpinRate: number;
  roundType: RoundType;
}> = ({
  spins,
  betAmount: rawBetAmount,
  lineAmount,
  onSpinClicked,
  buyFreeSpinRate: rawBuyFreeSpinRate,
  roundType,
}) => {
  const translator = useTranslator();
  const betAmount = BigNumber(rawBetAmount);
  const buyFreeSpinRate = BigNumber(rawBuyFreeSpinRate);
  const firstSpin = spins[0];
  const payout = spins.reduce((t, s) => t.plus(+s.winAmount), BigNumber(0));
  const elements = [
    <div
      key={`play-${firstSpin.playRefNumber}-id`}
      className="list-group-item list-group-item-primary text-center border fs-4"
    >
      {firstSpin.mode === "Base"
        ? translator.get("normal_spin_id")
        : translator.get("free_spin_id")}
      : <span className="fw-bold">{firstSpin.playRefNumber}</span>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-header`}
      className="list-group-item list-group-item-secondary"
    >
      <div className="d-flex text-center">
        <div className="col-4 border">{translator.get("spin_count")}</div>
        <div className="col-2 border">{translator.get("bet_size")}</div>
        <div className="col-3 border">{translator.get("payout")}</div>
        <div className="col-3 border">{translator.get("profit")}</div>
      </div>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-content`}
      className="list-group-item text-center border border"
    >
      <div className="d-flex">
        <div className="col-4 border">{spins.length}</div>
        <div className="col-2 border">
          {Utils.asCurrency(
            betAmount.multipliedBy(lineRatio).dividedBy(buyFreeSpinRate),
            2
          )}
        </div>
        <div className="col-3 border">{Utils.asCurrency(payout)}</div>
        <div className="col-3 border">
          {Utils.asCurrency(
            firstSpin.reelMatrix.length > 1 &&
              firstSpin.mode === "Base" &&
              roundType !== RoundType.FreePlay
              ? payout.minus(betAmount).dividedBy(buyFreeSpinRate)
              : payout,
            2
          )}
        </div>
      </div>
    </div>,
  ] as ReactElement[];

  spins.forEach((s) => {
    elements.push(
      <SpinRecord
        key={s.id}
        spin={s}
        lineAmount={lineAmount}
        onSpinClicked={onSpinClicked}
      />
    );
  });
  return <div className="mb-2">{elements}</div>;
};

export const SpinRecord: FunctionComponent<{
  lineAmount: number;
  spin: SpinHistory;
  onSpinClicked: (id: string) => void;
}> = ({ spin, lineAmount, onSpinClicked }) => {
  const gameData = (spin.gameData as DearSenpaiGameData) ?? {};
  const animationGroups = gameData.animationGroup ?? {};

  const translator = useTranslator();
  let payoutElementList: ReactElement[] = [
    <div
      key={`spin-${spin.id}-header`}
      className="list-group-item list-group-item-secondary"
    >
      <div className="d-flex flex-wrap text-center">
        <div className="col-2 border">{translator.get("symbol")}</div>
        <div className="col-3 border">{translator.get("line")}</div>
        <div className="col-2 border">{translator.get("rate")}</div>
        <div className="col-2 border">{translator.get("multiplier")}</div>
        <div className="col-3">{translator.get("payout")}</div>
      </div>
    </div>,
  ];
  if (spin.mode === "Select") {
    payoutElementList.push(
      <div key={`payout-${spin.id}-select`} className="d-flex">
        <div className="col-12 d-flex justify-content-center align-items-center">
          <div className="symbol">
            {GameUtils.convertSymbol(Symbols.Wild, gameData.freeSpinMode)}
          </div>
          {translator.get("selected")}
        </div>
      </div>
    );
  } else if (!spin.payout || spin.payout.length === 0) {
    if ((gameData.scatterPoints ?? []).length >= 3) {
      payoutElementList.push(
        <div key={`payout-${spin.id}-free`} className="d-flex">
          <div className="col-12 col-md-12">
            <b>{translator.get("bonus")}</b>
          </div>
        </div>
      );
    } else {
      payoutElementList = [
        <div key={`payout-${spin.id}-trigger-symbol`} className="d-flex">
          <div className="col-12 col-md-12">{translator.get("end")}</div>
        </div>,
      ];
    }
  } else if (Object.keys(animationGroups).length > 0) {
    payoutElementList.push(
      ...Object.entries(animationGroups).map((p, i) => {
        const [key, line] = p;
        const { lineDraw, symbolId } = GameUtils.convertLine(
          spin.reelMatrix,
          +key,
          line
        );
        const symbolPayout = Utils.changeFraction(
          BigNumber(line.beforeWin).dividedBy(lineAmount)
        );
        const bonusPayout =
          line.win === line.beforeWin
            ? "-"
            : "x" +
              Utils.changeFraction(
                BigNumber(line.win).dividedBy(line.beforeWin)
              );

        return (
          <div
            key={`payout-${spin.id}-${i}`}
            className="d-flex flex-wrap align-items-center"
          >
            <div className="col-2 d-flex justify-content-center ">
              <div className="symbol">
                {GameUtils.convertSymbol(symbolId, gameData.freeSpinMode)}
              </div>
            </div>
            <div className="col-3">{lineDraw}</div>
            <div className="col-2">x{symbolPayout}</div>
            <div className="col-2">{bonusPayout}</div>
            <div className="col-3">{Utils.asCurrency(line.win)}</div>
          </div>
        );
      })
    );
    payoutElementList.push(
      <div className="px-4" key={`payout-${spin.id}-hr`}>
        <hr />
      </div>
    );
    payoutElementList.push(
      <div
        key={`payout-${spin.id}-total`}
        className="d-flex align-items-center"
      >
        <div className="col-5"></div>
        <div className="col-4 fw-bold">{translator.get("win")}:</div>
        <div className="col-3 fs-6 fw-bold">
          {Utils.asCurrency(+spin.winAmount, 2)}
        </div>
      </div>
    );
  }
  let content = (
    <div className="d-flex flex-wrap text-center">
      <div className="col-12 col-md-6  py-3">
        <Reel
          reelMatrix={spin.reelMatrix}
          points={gameData.animationGroup}
          freeMode={gameData.freeSpinMode}
          scatterPoints={gameData.scatterPoints || []}
        />
      </div>
      <div
        className={`col-12 col-md-6 pb-3 ${
          spin.focus ? " list-group-item-success" : ""
        }`}
      >
        {payoutElementList}
      </div>
    </div>
  );
  if (!spin.reelMatrix || spin.reelMatrix.length === 0) {
    content = (
      <div className="d-flex flex-wrap text-center ">
        <div className="col-12 col-md-12 pb-3">{payoutElementList}</div>
      </div>
    );
  }

  return (
    <Link
      to={{ hash: spin.id }}
      id={spin.id}
      type="button"
      onClick={() => onSpinClicked(spin.id)}
      key={`spin-${spin.id}-id`}
      className={`list-group-item list-group-item-action border${
        spin.focus ? " border-success" : ""
      }`}
    >
      {content}
    </Link>
  );
};

export const Reel: FunctionComponent<{
  reelMatrix: number[][];
  points: { [k: number]: MatchedLine };
  freeMode: FreeSpinMode;
  scatterPoints: Point[];
}> = ({ reelMatrix, points, freeMode, scatterPoints }) => {
  const destoryPoints = new Set(
    Object.values(points || {}).flatMap((line) =>
      line.points.map((p) => `${p.x}-${p.y}`)
    )
  );

  const stackedX = new Set(
    reelMatrix
      .map((col, i) =>
        col.every((symbol) => symbol === Symbols.Wild) ? i : -1
      )
      .filter((x) => x >= 0)
  );

  if (destoryPoints.size === 0) {
    scatterPoints?.forEach((p) => {
      destoryPoints.add(`${p.x}-${p.y}`);
    });
  }

  const reelColumn = reelMatrix.reduce((col, rawCol, x) => {
    const colElements = rawCol.reduce((row, rawSymbol, y) => {
      const key = `${x}-${y}`;
      if (stackedX.has(x) && y > 0) {
        return row; // skip stacked wilds
      }
      row.push(
        <BoardCell
          key={key}
          symbol={rawSymbol}
          freeMode={freeMode}
          destory={
            stackedX.has(x) ? destoryPoints.size > 0 : destoryPoints.has(key)
          }
          stacked={y === 0 && stackedX.has(x)}
        />
      );
      return row;
    }, [] as ReactElement[]);
    col.push(
      <div key={"col-" + x} className="d-flex flex-column">
        {colElements}
      </div>
    );
    return col;
  }, [] as ReactElement[]);

  return <div className="d-flex justify-content-center">{reelColumn}</div>;
};

const BoardCell: FunctionComponent<{
  symbol: Symbols;
  freeMode: FreeSpinMode;
  destory: boolean;
  stacked: boolean;
}> = ({ symbol, freeMode, destory, stacked }) => {
  const classes = ["symbol", "flex", "border", "border-dark"];
  let cellContent = GameUtils.convertSymbol(symbol, freeMode);
  if (destory) {
    classes.push("animation");
  }
  if (stacked) {
    classes.push("stacked");
    cellContent = GameUtils.getStackWild(freeMode);
  }
  return <div className={classes.join(" ")}>{cellContent}</div>;
};
